import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";

const MENU_OFFSET = 6;
const VIEWPORT_PAD = 8;
const MENU_WIDTH = 260;
const FALLBACK_HEIGHT = 360;

/**
 * Renders action menus on document.body so backdrop-filter samples the full
 * page (same behaviour as header search / notification panes).
 * Opens below the anchor, right-aligned to the ⋯ (flips above if needed).
 */
export default function PortalInteractionMenu({
  show,
  anchorRef,
  menuRef,
  className = "",
  children,
}) {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!show || !anchorRef?.current) {
      setCoords(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const menuEl = menuRef?.current;
      const maxWidth = Math.min(280, window.innerWidth - VIEWPORT_PAD * 2);
      const measured = menuEl?.offsetWidth || MENU_WIDTH;
      const menuWidth = Math.min(Math.max(measured, MENU_WIDTH), maxWidth);
      const menuHeight = menuEl?.offsetHeight || FALLBACK_HEIGHT;

      let top = rect.bottom + MENU_OFFSET;
      let left = rect.right - menuWidth;

      if (top + menuHeight > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(
          VIEWPORT_PAD,
          rect.top - menuHeight - MENU_OFFSET
        );
      }

      left = Math.min(
        Math.max(VIEWPORT_PAD, left),
        window.innerWidth - menuWidth - VIEWPORT_PAD
      );

      setCoords({ top, left, width: menuWidth });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [show, anchorRef, menuRef]);

  if (!show || !coords) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={`dropdown-menu show InteractionModal wlj-portal-interaction-menu ${className}`.trim()}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        right: "auto",
        bottom: "auto",
        width: coords.width,
        maxWidth: "min(280px, calc(100vw - 24px))",
        transform: "none",
        display: "block",
      }}
    >
      {children}
    </div>,
    document.body
  );
}
